import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL === '/api' ? '' : API_URL;

// Types
interface DocTag {
  _id: string;
  name: string;
  color: string;
  description?: string;
}

interface AdminDoc {
  _id: string;
  title: string;
  content: string;
  summary?: string;
  tags: DocTag[];
  isPinned: boolean;
  isPublished: boolean;
  viewCount: number;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  lastEditedBy?: string;
}

// Styled Components
const Container = styled.div`
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 1.8rem;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const Button = styled(motion.button)<{ $variant?: 'primary' | 'secondary' | 'danger' | 'success' }>`
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  border: none;
  
  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%);
          color: white;
          &:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(76, 110, 245, 0.4); }
        `;
      case 'danger':
        return `
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          &:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4); }
        `;
      case 'success':
        return `
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          &:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4); }
        `;
      default:
        return `
          background: white;
          color: #374151;
          border: 1px solid #d1d5db;
          &:hover { background: #f3f4f6; }
        `;
    }
  }}
`;

const TabBar = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 8px;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 10px 20px;
  border: none;
  background: ${props => props.$active ? '#4c6ef5' : 'transparent'};
  color: ${props => props.$active ? 'white' : '#64748b'};
  border-radius: 8px 8px 0 0;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.$active ? '#4c6ef5' : '#f1f5f9'};
  }
`;

const SearchBar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 250px;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 0.95rem;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #4c6ef5;
    box-shadow: 0 0 0 3px rgba(76, 110, 245, 0.1);
  }
`;

const TagFilter = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

const TagChip = styled.span<{ $color: string; $selected?: boolean }>`
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$selected ? props.$color : `${props.$color}20`};
  color: ${props => props.$selected ? 'white' : props.$color};
  border: 2px solid ${props => props.$color};
  
  &:hover {
    transform: scale(1.05);
  }
`;

const DocGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
`;

const DocCard = styled(motion.div)<{ $isPinned?: boolean }>`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 2px solid ${props => props.$isPinned ? '#fbbf24' : 'transparent'};
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }
  
  ${props => props.$isPinned && `
    &::before {
      content: '📌';
      position: absolute;
      top: 12px;
      right: 12px;
      font-size: 1.2rem;
    }
  `}
`;

const DocTitle = styled.h3`
  font-size: 1.2rem;
  color: #1e293b;
  margin: 0 0 12px 0;
  padding-right: 30px;
`;

const DocSummary = styled.p`
  color: #64748b;
  font-size: 0.9rem;
  margin: 0 0 16px 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const DocMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
  color: #94a3b8;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
`;

const DocTags = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const SmallTag = styled.span<{ $color: string }>`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  background: ${props => `${props.$color}15`};
  color: ${props => props.$color};
`;

// Modal Styles
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)<{ $size?: 'small' | 'medium' | 'large' }>`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: ${props => {
    switch (props.$size) {
      case 'small': return '480px';
      case 'large': return '1000px';
      default: return '700px';
    }
  }};
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.3rem;
  color: #1e293b;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #94a3b8;
  padding: 4px;
  line-height: 1;
  
  &:hover { color: #1e293b; }
`;

const ModalBody = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
`;

const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

// Form Styles
const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 0.95rem;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #4c6ef5;
    box-shadow: 0 0 0 3px rgba(76, 110, 245, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 0.95rem;
  min-height: 300px;
  resize: vertical;
  font-family: 'Monaco', 'Consolas', monospace;
  line-height: 1.6;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #4c6ef5;
    box-shadow: 0 0 0 3px rgba(76, 110, 245, 0.1);
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  color: #374151;
  
  input {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
`;

const TagSelector = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding: 12px;
  background: #f8fafc;
  border-radius: 10px;
  border: 2px solid #e5e7eb;
`;

// Doc Viewer
const DocViewer = styled.div`
  .markdown-content {
    line-height: 1.8;
    color: #334155;
    
    h1 { font-size: 2rem; color: #1e293b; margin: 1.5em 0 0.5em; border-bottom: 2px solid #e5e7eb; padding-bottom: 0.5em; }
    h2 { font-size: 1.5rem; color: #334155; margin: 1.3em 0 0.5em; }
    h3 { font-size: 1.2rem; color: #475569; margin: 1.2em 0 0.4em; }
    
    p { margin: 1em 0; }
    
    ul, ol { margin: 1em 0; padding-left: 2em; }
    li { margin: 0.5em 0; }
    
    code {
      background: #f1f5f9;
      padding: 2px 6px;
      border-radius: 4px;
      font-family: 'Monaco', 'Consolas', monospace;
      font-size: 0.9em;
      color: #e11d48;
    }
    
    pre {
      background: #1e293b;
      color: #e2e8f0;
      padding: 16px 20px;
      border-radius: 10px;
      overflow-x: auto;
      margin: 1em 0;
      
      code {
        background: transparent;
        color: inherit;
        padding: 0;
      }
    }
    
    blockquote {
      border-left: 4px solid #4c6ef5;
      padding-left: 16px;
      margin: 1em 0;
      color: #64748b;
      font-style: italic;
    }
    
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 1em 0;
      
      th, td {
        border: 1px solid #e5e7eb;
        padding: 10px 14px;
        text-align: left;
      }
      
      th {
        background: #f8fafc;
        font-weight: 600;
      }
    }
    
    hr {
      border: none;
      border-top: 2px solid #e5e7eb;
      margin: 2em 0;
    }
    
    strong { color: #1e293b; }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #94a3b8;
  
  svg {
    width: 80px;
    height: 80px;
    margin-bottom: 20px;
    opacity: 0.5;
  }
  
  h3 {
    font-size: 1.3rem;
    color: #64748b;
    margin: 0 0 8px 0;
  }
  
  p {
    margin: 0;
  }
`;

const ColorPicker = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ColorOption = styled.button<{ $color: string; $selected: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 3px solid ${props => props.$selected ? '#1e293b' : 'transparent'};
  background: ${props => props.$color};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    transform: scale(1.1);
  }
`;

const TAG_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#22c55e',
  '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6',
  '#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899',
  '#f43f5e', '#64748b', '#71717a'
];

// Main Component
const AdminDocs: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'docs' | 'tags'>('docs');
  const [docs, setDocs] = useState<AdminDoc[]>([]);
  const [tags, setTags] = useState<DocTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTagFilter, setSelectedTagFilter] = useState<string | null>(null);
  
  // Modal states
  const [showDocModal, setShowDocModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState<AdminDoc | null>(null);
  const [editingTag, setEditingTag] = useState<DocTag | null>(null);
  const [viewingDoc, setViewingDoc] = useState<AdminDoc | null>(null);
  
  // Form states
  const [docForm, setDocForm] = useState({
    title: '',
    content: '',
    summary: '',
    tags: [] as string[],
    isPinned: false,
    isPublished: true
  });
  
  const [tagForm, setTagForm] = useState({
    name: '',
    color: '#4c6ef5',
    description: ''
  });
  
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Fetch data
  const fetchDocs = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/docs`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        setDocs(data.docs);
      }
    } catch (error) {
      console.error('Error fetching docs:', error);
    }
  }, []);

  const fetchTags = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/doc-tags`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        setTags(data.tags);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDocs(), fetchTags()]);
      setLoading(false);
    };
    loadData();
  }, [fetchDocs, fetchTags]);

  // Seed security doc
  const seedSecurityDoc = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_BASE}/api/admin/docs/seed-security-doc`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: data.message });
        fetchDocs();
        fetchTags();
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error seeding security documentation' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  // Doc CRUD
  const openDocEditor = (doc?: AdminDoc) => {
    if (doc) {
      setEditingDoc(doc);
      setDocForm({
        title: doc.title,
        content: doc.content,
        summary: doc.summary || '',
        tags: doc.tags.map(t => t._id),
        isPinned: doc.isPinned,
        isPublished: doc.isPublished
      });
    } else {
      setEditingDoc(null);
      setDocForm({
        title: '',
        content: '',
        summary: '',
        tags: [],
        isPinned: false,
        isPublished: true
      });
    }
    setShowDocModal(true);
  };

  const saveDoc = async () => {
    if (!docForm.title.trim() || !docForm.content.trim()) {
      setMessage({ type: 'error', text: 'Title and content are required' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    
    setSaving(true);
    try {
      const url = editingDoc 
        ? `${API_BASE}/api/admin/docs/${editingDoc._id}`
        : `${API_BASE}/api/admin/docs`;
      
      const response = await fetch(url, {
        method: editingDoc ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(docForm)
      });
      
      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: data.message });
        setShowDocModal(false);
        fetchDocs();
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving document' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const deleteDoc = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/docs/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: 'Document deleted' });
        fetchDocs();
        if (viewingDoc?._id === id) setShowViewModal(false);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error deleting document' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  // Tag CRUD
  const openTagEditor = (tag?: DocTag) => {
    if (tag) {
      setEditingTag(tag);
      setTagForm({
        name: tag.name,
        color: tag.color,
        description: tag.description || ''
      });
    } else {
      setEditingTag(null);
      setTagForm({ name: '', color: '#4c6ef5', description: '' });
    }
    setShowTagModal(true);
  };

  const saveTag = async () => {
    if (!tagForm.name.trim()) {
      setMessage({ type: 'error', text: 'Tag name is required' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    
    setSaving(true);
    try {
      const url = editingTag 
        ? `${API_BASE}/api/admin/doc-tags/${editingTag._id}`
        : `${API_BASE}/api/admin/doc-tags`;
      
      const response = await fetch(url, {
        method: editingTag ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(tagForm)
      });
      
      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: data.message });
        setShowTagModal(false);
        fetchTags();
        fetchDocs(); // Refresh docs to show updated tags
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving tag' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const deleteTag = async (id: string) => {
    if (!window.confirm('Are you sure? This tag will be removed from all documents.')) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/doc-tags/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: 'Tag deleted' });
        fetchTags();
        fetchDocs();
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error deleting tag' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  // View doc
  const viewDoc = async (doc: AdminDoc) => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/docs/${doc._id}`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        setViewingDoc(data.doc);
        setShowViewModal(true);
      }
    } catch (error) {
      console.error('Error loading document:', error);
    }
  };

  // Filter docs
  const filteredDocs = docs.filter(doc => {
    const matchesSearch = !searchQuery || 
      doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (doc.summary && doc.summary.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesTag = !selectedTagFilter || doc.tags.some(t => t._id === selectedTagFilter);
    return matchesSearch && matchesTag;
  });

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
          <div style={{ fontSize: '2rem', marginBottom: '16px' }}>📚</div>
          Loading documentation...
        </div>
      </Container>
    );
  }

  return (
    <Container>
      {/* Header */}
      <Header>
        <Title>
          <span style={{ fontSize: '1.8rem' }}>📚</span>
          Documentation
        </Title>
        <ActionButtons>
          {docs.length === 0 && (
            <Button $variant="success" onClick={seedSecurityDoc} disabled={saving}>
              🔒 Add Security Doc
            </Button>
          )}
          <Button $variant="primary" onClick={() => openDocEditor()}>
            ➕ New Document
          </Button>
          <Button onClick={() => openTagEditor()}>
            🏷️ New Tag
          </Button>
        </ActionButtons>
      </Header>

      {/* Message Toast */}
      <AnimatePresence>
        {message && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              padding: '14px 24px',
              borderRadius: '10px',
              background: message.type === 'success' ? '#10b981' : '#ef4444',
              color: 'white',
              fontWeight: 600,
              zIndex: 2000,
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
            }}
          >
            {message.type === 'success' ? '✅' : '❌'} {message.text}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tabs */}
      <TabBar>
        <Tab $active={activeTab === 'docs'} onClick={() => setActiveTab('docs')}>
          📄 Documents ({docs.length})
        </Tab>
        <Tab $active={activeTab === 'tags'} onClick={() => setActiveTab('tags')}>
          🏷️ Tags ({tags.length})
        </Tab>
      </TabBar>

      {/* Docs Tab */}
      {activeTab === 'docs' && (
        <>
          <SearchBar>
            <SearchInput
              type="text"
              placeholder="🔍 Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <TagFilter>
              <TagChip
                $color="#64748b"
                $selected={!selectedTagFilter}
                onClick={() => setSelectedTagFilter(null)}
              >
                All
              </TagChip>
              {tags.map(tag => (
                <TagChip
                  key={tag._id}
                  $color={tag.color}
                  $selected={selectedTagFilter === tag._id}
                  onClick={() => setSelectedTagFilter(selectedTagFilter === tag._id ? null : tag._id)}
                >
                  {tag.name}
                </TagChip>
              ))}
            </TagFilter>
          </SearchBar>

          {filteredDocs.length === 0 ? (
            <EmptyState>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3>No documents found</h3>
              <p>Create your first document to get started</p>
            </EmptyState>
          ) : (
            <DocGrid>
              {filteredDocs.map(doc => (
                <DocCard
                  key={doc._id}
                  $isPinned={doc.isPinned}
                  onClick={() => viewDoc(doc)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <DocTags>
                    {doc.tags.map(tag => (
                      <SmallTag key={tag._id} $color={tag.color}>{tag.name}</SmallTag>
                    ))}
                  </DocTags>
                  <DocTitle>{doc.title}</DocTitle>
                  <DocSummary>{doc.summary || doc.content.substring(0, 150)}...</DocSummary>
                  <DocMeta>
                    <span>👁️ {doc.viewCount} views</span>
                    <span>{new Date(doc.updatedAt).toLocaleDateString()}</span>
                  </DocMeta>
                </DocCard>
              ))}
            </DocGrid>
          )}
        </>
      )}

      {/* Tags Tab */}
      {activeTab === 'tags' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
          {tags.length === 0 ? (
            <EmptyState>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              <h3>No tags yet</h3>
              <p>Create tags to organize your documentation</p>
            </EmptyState>
          ) : (
            tags.map(tag => (
              <motion.div
                key={tag._id}
                style={{
                  background: 'white',
                  padding: '20px',
                  borderRadius: '12px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                  borderLeft: `4px solid ${tag.color}`
                }}
                whileHover={{ y: -2 }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontWeight: 700, fontSize: '1.1rem', color: tag.color }}>{tag.name}</span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => openTagEditor(tag)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => deleteTag(tag._id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                {tag.description && (
                  <p style={{ margin: 0, color: '#64748b', fontSize: '0.85rem' }}>{tag.description}</p>
                )}
                <p style={{ margin: '8px 0 0', color: '#94a3b8', fontSize: '0.8rem' }}>
                  {docs.filter(d => d.tags.some(t => t._id === tag._id)).length} documents
                </p>
              </motion.div>
            ))
          )}
        </div>
      )}

      {/* Doc Editor Modal */}
      <AnimatePresence>
        {showDocModal && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowDocModal(false)}
          >
            <ModalContent
              $size="large"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>{editingDoc ? '✏️ Edit Document' : '📄 New Document'}</ModalTitle>
                <CloseButton onClick={() => setShowDocModal(false)}>&times;</CloseButton>
              </ModalHeader>
              <ModalBody>
                <FormGroup>
                  <Label>Title *</Label>
                  <Input
                    type="text"
                    placeholder="Enter document title..."
                    value={docForm.title}
                    onChange={(e) => setDocForm({ ...docForm, title: e.target.value })}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Summary (optional)</Label>
                  <Input
                    type="text"
                    placeholder="Brief description for the card view..."
                    value={docForm.summary}
                    onChange={(e) => setDocForm({ ...docForm, summary: e.target.value })}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Tags</Label>
                  <TagSelector>
                    {tags.length === 0 ? (
                      <span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>No tags available. Create some first.</span>
                    ) : (
                      tags.map(tag => (
                        <TagChip
                          key={tag._id}
                          $color={tag.color}
                          $selected={docForm.tags.includes(tag._id)}
                          onClick={() => {
                            if (docForm.tags.includes(tag._id)) {
                              setDocForm({ ...docForm, tags: docForm.tags.filter(t => t !== tag._id) });
                            } else {
                              setDocForm({ ...docForm, tags: [...docForm.tags, tag._id] });
                            }
                          }}
                        >
                          {tag.name}
                        </TagChip>
                      ))
                    )}
                  </TagSelector>
                </FormGroup>
                <FormGroup>
                  <Label>Content * (Markdown supported)</Label>
                  <TextArea
                    placeholder="Write your documentation content here... Markdown is supported!"
                    value={docForm.content}
                    onChange={(e) => setDocForm({ ...docForm, content: e.target.value })}
                  />
                </FormGroup>
                <CheckboxGroup>
                  <CheckboxLabel>
                    <input
                      type="checkbox"
                      checked={docForm.isPinned}
                      onChange={(e) => setDocForm({ ...docForm, isPinned: e.target.checked })}
                    />
                    📌 Pin to top
                  </CheckboxLabel>
                  <CheckboxLabel>
                    <input
                      type="checkbox"
                      checked={docForm.isPublished}
                      onChange={(e) => setDocForm({ ...docForm, isPublished: e.target.checked })}
                    />
                    ✅ Published
                  </CheckboxLabel>
                </CheckboxGroup>
              </ModalBody>
              <ModalFooter>
                <Button onClick={() => setShowDocModal(false)}>Cancel</Button>
                <Button $variant="primary" onClick={saveDoc} disabled={saving}>
                  {saving ? '⏳ Saving...' : '💾 Save Document'}
                </Button>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {/* Tag Editor Modal */}
      <AnimatePresence>
        {showTagModal && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowTagModal(false)}
          >
            <ModalContent
              $size="small"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>{editingTag ? '✏️ Edit Tag' : '🏷️ New Tag'}</ModalTitle>
                <CloseButton onClick={() => setShowTagModal(false)}>&times;</CloseButton>
              </ModalHeader>
              <ModalBody>
                <FormGroup>
                  <Label>Tag Name *</Label>
                  <Input
                    type="text"
                    placeholder="e.g., App Security, UI, Database..."
                    value={tagForm.name}
                    onChange={(e) => setTagForm({ ...tagForm, name: e.target.value })}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Color</Label>
                  <ColorPicker>
                    {TAG_COLORS.map(color => (
                      <ColorOption
                        key={color}
                        $color={color}
                        $selected={tagForm.color === color}
                        onClick={() => setTagForm({ ...tagForm, color })}
                      />
                    ))}
                  </ColorPicker>
                </FormGroup>
                <FormGroup>
                  <Label>Description (optional)</Label>
                  <Input
                    type="text"
                    placeholder="Brief description of this tag..."
                    value={tagForm.description}
                    onChange={(e) => setTagForm({ ...tagForm, description: e.target.value })}
                  />
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button onClick={() => setShowTagModal(false)}>Cancel</Button>
                <Button $variant="primary" onClick={saveTag} disabled={saving}>
                  {saving ? '⏳ Saving...' : '💾 Save Tag'}
                </Button>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {/* Doc Viewer Modal */}
      <AnimatePresence>
        {showViewModal && viewingDoc && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowViewModal(false)}
          >
            <ModalContent
              $size="large"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <div>
                  <ModalTitle>{viewingDoc.title}</ModalTitle>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    {viewingDoc.tags.map(tag => (
                      <SmallTag key={tag._id} $color={tag.color}>{tag.name}</SmallTag>
                    ))}
                  </div>
                </div>
                <CloseButton onClick={() => setShowViewModal(false)}>&times;</CloseButton>
              </ModalHeader>
              <ModalBody>
                <DocViewer>
                  <div className="markdown-content">
                    <ReactMarkdown>
                      {viewingDoc.content}
                    </ReactMarkdown>
                  </div>
                </DocViewer>
              </ModalBody>
              <ModalFooter>
                <div style={{ flex: 1, color: '#94a3b8', fontSize: '0.85rem' }}>
                  Last updated: {new Date(viewingDoc.updatedAt).toLocaleString()} | Views: {viewingDoc.viewCount}
                </div>
                <Button $variant="danger" onClick={() => deleteDoc(viewingDoc._id)}>
                  🗑️ Delete
                </Button>
                <Button $variant="primary" onClick={() => { setShowViewModal(false); openDocEditor(viewingDoc); }}>
                  ✏️ Edit
                </Button>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </Container>
  );
};

export default AdminDocs;
